
import React, { createContext } from 'react';
import { initializeApp, getApp, getApps } from "firebase/app";
import { getDatabase, ref, query, orderByChild, equalTo } from "firebase/database";
import { initializeAuth, getAuth, GoogleAuthProvider, OAuthProvider, signInWithPhoneNumber, PhoneAuthProvider, RecaptchaVerifier, unlink, updatePhoneNumber, linkWithPhoneNumber, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { getStorage, ref as stRef } from "firebase/storage";

const FirebaseContext = createContext(null);

let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
    config: null
}

const createFullStructure = (app, db, auth, storage, config) => {
    let googleProvider = null;
    try {
        if (typeof GoogleAuthProvider === 'function') {
            googleProvider = new GoogleAuthProvider();
        }
    } catch (e) {
        googleProvider = null;
    }

    let appleProvider = null;
    try {
        if (typeof OAuthProvider === 'function') {
            appleProvider = new OAuthProvider('apple.com');
        }
    } catch (e) {
        appleProvider = null;
    }

    let phoneProvider = null;
    try {
        if (auth && typeof PhoneAuthProvider === 'function') {
            phoneProvider = new PhoneAuthProvider(auth);
        }
    } catch (e) {
        // PhoneAuthProvider is not supported in React Native / non-browser environment
        phoneProvider = null;
    }

    let internalAuth = auth;

    const s = {
        app: app,
        config: config,
        database: db,
        get auth() {
            if (internalAuth) return internalAuth;
            return this.authRef();
        },
        set auth(val) {
            internalAuth = val;
        },
        storage: storage,
        authRef: () => {
            try {
                if (internalAuth) return internalAuth;
                if (app) {
                    try {
                        const rn = require("@firebase/auth");
                        return (rn.getAuth || getAuth)(app);
                    } catch (_) {
                        return getAuth(app);
                    }
                }
                return internalAuth;
            } catch (e) {
                return internalAuth;
            }
        },
        googleProvider: googleProvider,
        appleProvider: appleProvider,
        phoneProvider: phoneProvider,
        RecaptchaVerifier: RecaptchaVerifier,
        signInWithPhoneNumber: signInWithPhoneNumber,
        updatePhoneNumber: updatePhoneNumber,
        unlink: unlink,
        googleCredential: (idToken, accessToken) => GoogleAuthProvider.credential(idToken, accessToken),
        linkWithPhoneNumber: linkWithPhoneNumber,
        mobileAuthCredential: (verificationId, code) => PhoneAuthProvider.credential(verificationId, code),
    };

    if (db) {
        Object.assign(s, {
            usersRef: ref(db, 'users'),
            driversRef: query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")),
            bookingRef: ref(db, 'bookings'),
            cancelreasonRef: ref(db, 'cancel_reason'),
            settingsRef: ref(db, 'settings'),
            smtpRef: ref(db, "smtpdata"),
            smsRef: ref(db, "smsConfig"),
            carTypesRef: ref(db, 'cartypes'),
            carTypesEditRef: (id) => ref(db, "cartypes/" + id),
            promoRef: ref(db, 'promos'),
            promoEditRef: (id) => ref(db, "promos/" + id),
            notifyRef: ref(db, "notifications/"),
            notifyEditRef: (id) => ref(db, "notifications/" + id),
            addressRef: (uid, id) => ref(db, "savedAddresses/" + uid + "/" + id),
            addressEditRef: (uid) => ref(db, "savedAddresses/" + uid),
            singleUserRef: (uid) => ref(db, "users/" + uid),
            singleBookingRef: (bookingKey) => ref(db, "bookings/" + bookingKey),
            requestedDriversRef: (bookingKey) => ref(db, "bookings/" + bookingKey + "/requestedDrivers"),
            referralIdRef: (referralId) => query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
            trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
            tasksRef: (uid) => uid ? ref(db, 'driver_tasks/' + uid) : null,
            singleTaskRef: (uid, bookingId) => ref(db, "driver_tasks/" + uid + "/" + bookingId),
            driverBookingsRef: (uid) => ref(db, 'driver_bookings/' + uid),
            singleDriverBookingRef: (uid, bookingKey) => ref(db, 'driver_bookings/' + uid + '/' + bookingKey),
            bookingsDriverRef: (uid) => query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid)),
            bookingsDriverIdRef: (uid) => query(ref(db, 'bookings'), orderByChild('driverId'), equalTo(uid)),
            bookingListRef: (uid, role) =>
                role == 'customer' ? query(ref(db, 'bookings'), orderByChild('customer_uid'), equalTo(uid)) :
                    (role == 'driver' ?
                        ref(db, 'driver_bookings/' + uid)
                        :
                        (role == 'fleetadmin' ?
                            query(ref(db, 'bookings'), orderByChild('fleetadmin'), equalTo(uid))
                            : ref(db, 'bookings')
                        )
                    ),
            chatRef: (bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
            withdrawRef: ref(db, 'withdraws/'),
            languagesRef: ref(db, "languages"),
            languagesEditRef: (id) => ref(db, "languages/" + id),
            langEditRef: (id) => ref(db, `languages/${id}/keyValuePairs/`),
            walletHistoryRef: (uid) => ref(db, "walletHistory/" + uid),
            userNotificationsRef: (uid) => ref(db, "userNotifications/" + uid),
            userRatingsRef: (uid) => ref(db, "userRatings/" + uid),
            carsRef: (uid, role) => role == 'driver' ?
                query(ref(db, 'cars'), orderByChild('driverId'), equalTo(uid))
                : (role == 'fleetadmin' ?
                    query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
                    : ref(db, 'cars')
                ),
            carsRefDriver: (uid) => query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid)),
            carsRefDriverId: (uid) => query(ref(db, 'cars'), orderByChild('driverId'), equalTo(uid)),
            carAddRef: ref(db, "cars"),
            carEditRef: (id) => ref(db, "cars/" + id),
            allLocationsRef: ref(db, "locations"),
            userLocationRef: (uid) => ref(db, "locations/" + uid),
            sosRef: ref(db, 'sos'),
            editSosRef: (id) => ref(db, "sos/" + id),
            complainRef: ref(db, 'complain'),
            editComplainRef: (id) => ref(db, "complain/" + id),
            zonesRef: ref(db, 'zones'),
            customRoutesRef: ref(db, 'custom_routes'),
            zoneToZoneRulesRef: ref(db, 'zone_to_zone_rules'),
            paymentSettingsRef: ref(db, "payment_settings"),
            telegramSettingsRef: ref(db, "telegram_settings"),
            telegramLinkTokensRef: ref(db, "telegram_link_tokens"),
            usedreferralRef: ref(db, 'usedreferral'),
        });
    }

    if (storage) {
        Object.assign(s, {
            carDocImage: (id) => stRef(storage, `cartypes/${id}`),
            profileImageRef: (uid) => stRef(storage, `users/${uid}/profileImage`),
            verifyIdImageRef: (uid) => stRef(storage, `users/${uid}/verifyIdImage`),
            bookingImageRef: (bookingId, imageType) => stRef(storage, `bookings/${bookingId}/${imageType}`),
            driverDocsRef: (uid) => stRef(storage, `users/${uid}/license`),
            driverDocsRefBack: (uid) => stRef(storage, `users/${uid}/licenseBack`),
            carImage: (id) => stRef(storage, `cars/${id}`),
        });
    }

    return s;
}

let initLock = false;

const FirebaseProvider = ({ config, children, AsyncStorage }) => {
    // [STABLE_INITIALIZATION] Double-render protection
    if (!firebase.app && !initLock) {
        initLock = true;
        try {
            //console.log("[FirebaseProvider] Starting Unified Initialization...");
            //console.log("[FirebaseProvider] databaseURL:", config?.databaseURL);
            const apps = getApps();
            let app = apps.length > 0 ? apps[0] : initializeApp(config);

            let auth;
            try {
                if (typeof document !== 'undefined') {
                    try {
                        auth = initializeAuth(app, {
                            persistence: browserLocalPersistence,
                            popupRedirectResolver: browserPopupRedirectResolver,
                        });
                    } catch (e) {
                        auth = getAuth(app);
                    }
                } else {
                    let rnAuthModule = null;
                    try {
                        rnAuthModule = require("@firebase/auth");
                    } catch (e) {
                        try {
                            rnAuthModule = require("firebase/auth");
                        } catch (e2) {}
                    }

                    const initAuthFn = rnAuthModule?.initializeAuth || initializeAuth;
                    const getAuthFn = rnAuthModule?.getAuth || getAuth;
                    const getPersistence = rnAuthModule?.getReactNativePersistence;

                    try {
                        if (typeof getPersistence === 'function' && AsyncStorage) {
                            try {
                                auth = initAuthFn(app, {
                                    persistence: getPersistence(AsyncStorage)
                                });
                            } catch (persistErr) {
                                console.warn("[FirebaseProvider] Persistence init warning:", persistErr?.message);
                                auth = typeof getAuthFn === 'function' ? getAuthFn(app) : initAuthFn(app);
                            }
                        } else if (typeof initAuthFn === 'function') {
                            auth = initAuthFn(app);
                        } else if (typeof getAuthFn === 'function') {
                            auth = getAuthFn(app);
                        }
                    } catch (initErr) {
                        try {
                            auth = typeof getAuthFn === 'function' ? getAuthFn(app) : null;
                        } catch (getErr) {
                            console.warn("[FirebaseProvider] getAuth fallback failed:", getErr?.message);
                            auth = null;
                        }
                    }
                }
            } catch (authErr) {
                console.warn("[FirebaseProvider] Auth outer error:", authErr?.message);
                try {
                    const rn = require("@firebase/auth");
                    auth = (rn.getAuth || rn.initializeAuth)(app);
                } catch (e2) {
                    try {
                        auth = getAuth(app);
                    } catch (e3) {
                        auth = null;
                    }
                }
            }

            if (!auth && app) {
                try {
                    auth = getAuth(app);
                } catch (_) {
                    try {
                        const rn = require("@firebase/auth");
                        auth = (rn.getAuth || rn.initializeAuth)(app);
                    } catch (_) {}
                }
            }

            let database = null;
            try {
                // [FIX] databaseURL is already in config, getDatabase(app) will use it automatically
                database = getDatabase(app);
            } catch (dbErr) {
                console.error("[FirebaseProvider] Database init failed:", dbErr);
            }

            let storage = null;
            try {
                storage = getStorage(app);
            } catch (stErr) {
                console.error("[FirebaseProvider] Storage init failed:", stErr);
            }

            // Update module singleton
            const structure = createFullStructure(app, database, auth, storage, config);
            Object.assign(firebase, structure);
            //console.log("[FirebaseProvider] Unified Initialization Complete.");
            //console.log("[FirebaseProvider] app.name:", app?.name);
            //console.log("[FirebaseProvider] has database?", !!database);
            //console.log("[FirebaseProvider] has storage?", !!storage);
            //console.log("[FirebaseProvider] config.databaseURL:", config?.databaseURL);
        } catch (error) {
            console.error("[FirebaseProvider] Fatal Init Error:", error);
        } finally {
            initLock = false;
        }
    }

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    )
}

export {
    firebase,
    FirebaseProvider,
    FirebaseContext
}